import React, { Fragment, useEffect } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Divider,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import {
  ArrowForward,
  CheckCircleOutline,
  ContentCopy,
  HomeOutlined
} from '@mui/icons-material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { getArticleBySlug } from '../content/articles';

const siteUrl = 'https://sterk-aanhetwerk.nl';

const setMetaTag = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  const created = !element;
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  const previous = Object.fromEntries(
    Object.keys(attributes).map((key) => [key, element?.getAttribute(key) ?? null])
  );
  Object.entries(attributes).forEach(([key, value]) => element?.setAttribute(key, value));

  return () => {
    if (created) {
      element?.remove();
    } else {
      Object.entries(previous).forEach(([key, value]) => {
        if (value === null) element?.removeAttribute(key);
        else element?.setAttribute(key, value);
      });
    }
  };
};

const RichParagraph = ({ text }: { text: string }) => {
  const parts = text.split(/(\[[^\]]+\]\(\/[^)]+\))/g);
  return (
    <Typography paragraph>
      {parts.map((part, index) => {
        const match = part.match(/^\[([^\]]+)\]\((\/[^)]+)\)$/);
        return match ? (
          <Link key={index} component={RouterLink} to={match[2]} fontWeight={600}>
            {match[1]}
          </Link>
        ) : (
          <Fragment key={index}>{part}</Fragment>
        );
      })}
    </Typography>
  );
};

const ConsultationCta = ({ compact = false }: { compact?: boolean }) => (
  <Paper
    component="aside"
    elevation={0}
    sx={{
      my: compact ? 5 : 7,
      p: { xs: 3, md: 4.5 },
      bgcolor: 'primary.dark',
      color: 'primary.contrastText',
      textAlign: 'center'
    }}
  >
    <Typography component="h2" variant="h3" sx={{ mb: 1.5 }}>
      Zullen we jouw loopbaanvraag samen verkennen?
    </Typography>
    <Typography sx={{ maxWidth: 680, mx: 'auto', mb: 3, color: 'inherit' }}>
      In een gratis en vrijblijvend kennismakingsgesprek van 30 minuten bespreken we waar
      je staat en wat jou verder kan helpen.
    </Typography>
    <Button
      component={RouterLink}
      to="/contact"
      state={{ subject: 'Gratis kennismakingsgesprek loopbaancoaching' }}
      variant="contained"
      color="secondary"
      endIcon={<ArrowForward />}
    >
      Plan je gratis kennismakingsgesprek
    </Button>
  </Paper>
);

const TipArticle = () => {
  const { slug = '' } = useParams();
  const article = getArticleBySlug(slug);

  useEffect(() => {
    if (!article) return;
    const pageTitle = `${article.title} | Sterk aan het Werk`;
    const canonicalUrl = `${siteUrl}/tips/${article.slug}`;
    const previousTitle = document.title;
    document.title = pageTitle;
    const cleanups = [
      setMetaTag('meta[name="description"]', {
        name: 'description',
        content: article.description
      }),
      setMetaTag('meta[name="keywords"]', { name: 'keywords', content: article.keyword }),
      setMetaTag('meta[property="og:title"]', { property: 'og:title', content: pageTitle }),
      setMetaTag('meta[property="og:description"]', {
        property: 'og:description',
        content: article.description
      }),
      setMetaTag('meta[property="og:type"]', { property: 'og:type', content: 'article' }),
      setMetaTag('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl }),
      setMetaTag('meta[property="article:published_time"]', {
        property: 'article:published_time',
        content: article.publishedAt
      }),
      setMetaTag('meta[property="article:modified_time"]', {
        property: 'article:modified_time',
        content: article.updatedAt
      }),
      setMetaTag('meta[name="twitter:card"]', {
        name: 'twitter:card',
        content: 'summary'
      }),
      setMetaTag('meta[name="twitter:title"]', { name: 'twitter:title', content: pageTitle }),
      setMetaTag('meta[name="twitter:description"]', {
        name: 'twitter:description',
        content: article.description
      })
    ];

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const createdCanonical = !canonical;
    const previousCanonical = canonical?.getAttribute('href');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    return () => {
      document.title = previousTitle;
      cleanups.forEach((cleanup) => cleanup());
      if (createdCanonical) canonical?.remove();
      else if (previousCanonical) canonical?.setAttribute('href', previousCanonical);
    };
  }, [article]);

  if (!article) {
    return (
      <Container component="main" maxWidth="md" sx={{ py: 10 }}>
        <Typography variant="h1">Artikel niet gevonden</Typography>
        <Button component={RouterLink} to="/tips" sx={{ mt: 3 }}>
          Terug naar alle tips
        </Button>
      </Container>
    );
  }

  const canonicalUrl = `${siteUrl}/tips/${article.slug}`;
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    datePublished: article.publishedAt,
    dateModified: article.updatedAt,
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
    author: {
      '@type': 'Person',
      name: 'Hanneke van Keulen',
      url: `${siteUrl}/over`
    },
    publisher: {
      '@type': 'Organization',
      name: 'Sterk aan het Werk',
      url: siteUrl,
      logo: { '@type': 'ImageObject', url: `${siteUrl}/logo_sterkaanhetwerk.jpg` }
    }
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Tips', item: `${siteUrl}/tips` },
      { '@type': 'ListItem', position: 3, name: article.title, item: canonicalUrl }
    ]
  };
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer }
    }))
  };

  return (
    <Box component="main" sx={{ bgcolor: 'background.default', pb: 8 }}>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <Box component="header" sx={{ bgcolor: 'primary.dark', color: 'white', py: { xs: 7, md: 10 } }}>
        <Container maxWidth="md">
          <Breadcrumbs
            aria-label="Broodkruimelpad"
            sx={{ mb: 3, '& a, & p, & .MuiBreadcrumbs-separator': { color: 'rgba(255,255,255,.82)' } }}
          >
            <Link component={RouterLink} to="/" underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
              <HomeOutlined sx={{ mr: 0.5, fontSize: 18 }} />
              Home
            </Link>
            <Link component={RouterLink} to="/tips" underline="hover">
              Tips
            </Link>
            <Typography color="inherit" aria-current="page">
              {article.title}
            </Typography>
          </Breadcrumbs>
          <Typography component="h1" variant="h1" sx={{ color: 'inherit', mb: 2 }}>
            {article.title}
          </Typography>
          <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,.9)', mb: 2 }}>
            {article.excerpt}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,.78)' }}>
            Door Hanneke van Keulen ·{' '}
            {new Intl.DateTimeFormat('nl-NL', { dateStyle: 'long' }).format(
              new Date(`${article.publishedAt}T12:00:00`)
            )}{' '}
            · {article.readTime} leestijd
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ mt: { xs: 4, md: 6 } }}>
        <Paper component="article" elevation={0} sx={{ p: { xs: 3, md: 6 } }}>
          {article.sections.map((section, sectionIndex) => (
            <Fragment key={section.heading}>
              <Box component="section" sx={{ mb: 5 }}>
                <Typography component="h2" variant="h2" sx={{ mb: 2.5 }}>
                  {section.heading}
                </Typography>
                {section.paragraphs.map((paragraph) => (
                  <RichParagraph key={paragraph} text={paragraph} />
                ))}
                {section.bullets && (
                  <List disablePadding sx={{ mb: 2 }}>
                    {section.bullets.map((bullet) => (
                      <ListItem key={bullet} alignItems="flex-start" disableGutters>
                        <ListItemIcon sx={{ minWidth: 34, mt: 0.5 }}>
                          <CheckCircleOutline color="secondary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={bullet} />
                      </ListItem>
                    ))}
                  </List>
                )}
                {section.emailTemplate && (
                  <Alert
                    icon={<ContentCopy />}
                    severity="info"
                    sx={{
                      mt: 3,
                      alignItems: 'flex-start',
                      '& .MuiAlert-message': { width: '100%' }
                    }}
                  >
                    <Typography fontWeight={700} sx={{ mb: 2 }}>
                      Onderwerp: {section.emailTemplate.subject}
                    </Typography>
                    <Typography
                      component="div"
                      sx={{ whiteSpace: 'pre-line', fontFamily: '"Poppins", sans-serif' }}
                    >
                      {section.emailTemplate.body}
                    </Typography>
                  </Alert>
                )}
              </Box>
              {sectionIndex === 3 && <ConsultationCta compact />}
            </Fragment>
          ))}

          <Divider />
          <Box component="section" aria-labelledby="veelgestelde-vragen" sx={{ mt: 5 }}>
            <Typography id="veelgestelde-vragen" component="h2" variant="h2" sx={{ mb: 3 }}>
              Veelgestelde vragen
            </Typography>
            <Stack spacing={2}>
              {article.faqs.map((faq) => (
                <Box key={faq.question}>
                  <Typography component="h3" variant="h4" sx={{ mb: 1 }}>
                    {faq.question}
                  </Typography>
                  <Typography>{faq.answer}</Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          <ConsultationCta />

          <Typography textAlign="center">
            Meer weten over de aanpak en tarieven? Lees alles over{' '}
            <Link component={RouterLink} to="/loopbaancoaching" fontWeight={600}>
              online loopbaancoaching
            </Link>
            .
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default TipArticle;
